import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useDeviceType, MOBILE_SCREEN } from '@travel/traveler-core/hooks';
import { cx } from '@travel/utils';

// below import is for sample. remove when integrating
import DataLayer from 'favoriteList/components/DataLayer';
import Filters from 'favoriteList/components/Filters';
import SearchForm from 'favoriteList/components/SearchForm';
import {
  FAVORITE_TAB_TITLE_DOMESTIC,
  FAVORITE_TAB_TITLE_OVERSEAS,
  SITE_IMAGE_URL,
} from 'favoriteList/constants/common';
import { getQueryString } from 'favoriteList/constants/filters';
import SEO from 'favoriteList/core/components/SEO';
import paths from 'favoriteList/core/universalRouter/paths';
import useScroll from 'favoriteList/hooks/useScroll';
import { pushLocation } from 'favoriteList/store/__router/actions';
import { fetchFilter } from 'favoriteList/store/providerPlan/actions';
import AccommodationPlanList from './components/AccommodationPlanList';
import BreadcrumbFavorite from './components/BreadcrumbFavorite';
import PageTitle from './components/PageTitle';
import ProviderList from './components/ProviderList';
import ProviderPlanTab from './components/ProviderPlanTab';
import TotalDataCount from './TotalDataCount';

import { getRatPage } from 'favoriteList/components/DataLayer/helper';
import type { Query } from 'ProviderPlan-Types';

import styles from './favoriteList.module.scss';

type Props = {
  path: string;
  search?: string;
  query: Query;
  isDomestic: boolean;
  isPlanList: boolean;
};

const FavoriteList = (props: Props) => {
  const { query, isDomestic, search, isPlanList } = props;
  const dispatch = useDispatch();
  const isSP = useDeviceType() === MOBILE_SCREEN;
  const favPageTitle = isDomestic ? FAVORITE_TAB_TITLE_DOMESTIC : FAVORITE_TAB_TITLE_OVERSEAS;
  const stickyHeader = useRef<HTMLDivElement>(null);
  const filtersContainer = useRef<HTMLDivElement>(null);
  const [shouldHideFilters, setShouldHideFilters] = useState(false);
  const [bottomMargin, setBottomMargin] = useState(0);
  const [bodyWidth, setBodyWidth] = useState(0);

  const scroll = useScroll({ threshold: 10 });

  useEffect(() => {
    dispatch(fetchFilter(isPlanList, isDomestic));
  }, [dispatch, isDomestic, isPlanList]);

  useEffect(() => {
    if (!stickyHeader.current || !filtersContainer.current) {
      return;
    }
    if (
      stickyHeader.current.getBoundingClientRect().top ===
      parseInt(getComputedStyle(stickyHeader.current).top)
    ) {
      if (scroll.direction === 'down' && scroll.isPassThreshold) {
        setShouldHideFilters(true);
        setBottomMargin(filtersContainer.current.offsetHeight);
      } else if (scroll.direction === 'up' && scroll.isPassThreshold) {
        setShouldHideFilters(false);
        setBottomMargin(0);
      }
    } else {
      setShouldHideFilters(false);
      setBottomMargin(0);
    }
  }, [scroll]);

  const onSearchCallback = (
    sortKey: string,
    filters: { searchKey?: string; featureId: string; isParent?: boolean }[],
  ) => {
    const queryString = getQueryString(sortKey, filters);
    const path = isDomestic
      ? isPlanList
        ? paths.domesticPlan.path
        : paths.domesticProvider.path
      : isPlanList
        ? paths.overseasPlan.path
        : paths.overseasProvider.path;

    dispatch(
      pushLocation({
        pathname: path,
        search: queryString,
      }),
    );
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.borderBoxSize.length > 0) {
          setBodyWidth(entry.borderBoxSize[0].inlineSize);
        }
      }
    });
    resizeObserver.observe(document.body);
    return () => resizeObserver.unobserve(document.body);
  });

  return (
    <div data-testid="favorite-list" className={styles.favoriteListWrapper}>
      <SEO title={favPageTitle} imageUrl={SITE_IMAGE_URL} />
      {!isSP && <BreadcrumbFavorite />}
      <PageTitle isSP={isSP} isDomestic={isDomestic} isPlanList={isPlanList} />
      {isSP && <ProviderPlanTab isDomestic={isDomestic} isPlanList={isPlanList} query={query} />}
      <div
        ref={stickyHeader}
        className={cx(styles.stickySearch, shouldHideFilters && styles.stickySearchHiddenFilters)}
        style={{ marginBottom: `${bottomMargin}px` }}
      >
        {!isPlanList && (
          <div className={styles.searchContainer}>
            <SearchForm isDomestic={isDomestic} />
          </div>
        )}
        <div
          ref={filtersContainer}
          className={cx(
            styles.filtersContainer,
            shouldHideFilters && styles.filtersContainerHide,
            isPlanList && styles.filtersContainerPlanList,
            stickyHeader.current &&
              stickyHeader.current.getBoundingClientRect().top ===
                parseInt(getComputedStyle(stickyHeader.current).top) &&
              styles.filtersContainerSticky,
          )}
        >
          <Filters
            onSearchCallback={onSearchCallback}
            isPlanList={isPlanList}
            search={search}
            isDomestic={isDomestic}
          />
        </div>
        <div style={{ width: `${bodyWidth}px` }} className={styles.stickySearchBorder} />
      </div>
      {isSP ? <TotalDataCount isPlanList={isPlanList} /> : null}
      {!isSP && <ProviderPlanTab isDomestic={isDomestic} isPlanList={isPlanList} query={query} />}
      {isPlanList ? (
        <AccommodationPlanList query={query} isDomestic={isDomestic} isPlanList={isPlanList} />
      ) : (
        <ProviderList query={query} isDomestic={isDomestic} isPlanList={isPlanList} />
      )}
      <DataLayer page={getRatPage(isDomestic, isPlanList)} />
    </div>
  );
};

export default FavoriteList;
